export enum LOCAL_STORAGE_KEY {
  /**
   * Stores complete QuizStateType object with comprehensive health, personal,
   * and preference data including weight, height, health conditions, medication
   * status, ethnicity, dietary preferences, and GLP1 eligibility information.
   */
  CONSUMER_QUIZ_RESULTS = 'cached-quiz',
  /**
   * Stores VitalityQuizStateType object similar to consumer quiz but includes
   * Vitality-specific fields like membership number, address details, and phone
   * number.
   */
  VITALITY_QUIZ_RESULTS = 'cached-vitality-msp-quiz',
  /**
   * Stores ReducerState object with personal details, health info, address,
   * payment details, and promo code state for existing users rejoining the
   * programme.
   */
  RESTART = 'cached-restart-data',
  /**
   * Stores MseStateType object for Mid & South Essex contract signup,
   * containing NHS referral info plus user details and health conditions.
   */
  MSE_QUIZ_RESULTS = 'cached-mse-sign-up',
  /**
   * Stores NHSStateType object with comprehensive NHS referral signup data
   * including referral details, health questionnaires, and NDPP-specific
   * fields.
   */
  NHS_SIGNUP = 'cached-sign-up',
  /**
   * Stores VitalityStateType object for Vitality member signup (distinct from
   * VITALITY_QUIZ_RESULTS).
   */
  VITALITY_SIGNUP = 'cached-sign-up-vitality',
  /**
   * Boolean flag indicating if user has a smartphone/tablet (default: true).
   * Used to determine which download instructions to show after NHS signup and
   * affects messaging about app vs web app usage.
   */
  HAS_SMART_PHONE = 'has-smart-phone',
  /**
   * Temporarily stores user email during restart flows and partnerships for
   * form pre-population, user identification, and deep linking with deferred
   * parameters.
   */
  EMAIL = 'email',
  /**
   * Boolean flag for users on hypoglycemic medication (default: true). Used in
   * NHS Type 2 diabetes partnership flow to determine navigation path and show
   * medication warnings for safety routing.
   */
  IS_ON_HYPO_MEDICATION = 'isOnHypoMedication',
  /**
   * Temporarily stores original height/weight/health condition values when user
   * clicks "Edit my details" on confirmation pages. Used to track which
   * specific fields changed after editing for analytics. Cleared after
   * confirmation to prevent stale data.
   */
  INITIAL_EDIT_VALUES = 'initialEditValues',
}

export const setLocalStorage = <T>(key: LOCAL_STORAGE_KEY, value: T): void => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
    // eslint-disable-next-line no-empty
  } catch (e) {}
};

export const getLocalStorage = <T>(
  key: LOCAL_STORAGE_KEY,
  initialValue: T,
): T => {
  try {
    const value = window.localStorage.getItem(key);
    return value ? JSON.parse(value) : initialValue;
  } catch (e) {
    return initialValue;
  }
};

export const clearLocalStorage = (key: LOCAL_STORAGE_KEY): void => {
  try {
    window.localStorage.removeItem(key);
    // eslint-disable-next-line no-empty
  } catch (e) {}
};
